// @flow
import cloneDeep from 'lodash.clonedeep';
import { trackEvent } from 'utils/analytics';
import { extractLocationProperties } from 'utils/getMainSearchTrackingData';
import {
  fetchCurrentUser as apiFetchCurrentUser,
  updateSearchLocation as apiUpdateSearchLocation,
  clearSearchLocation as apiClearSearchLocation,
  saveListing as apiSaveListing,
  unsaveListing as apiUnsaveListing,
  saveSearch as apiSaveSearch,
  unsaveSearch as apiUnsaveSearch,
  setABExperimentBucket as apiSetABExperimentBucket
} from 'api/currentUser';
import type {
  CurrentUser,
  SavedSearch,
  SavedSearchUnsaved
} from 'api/currentUser';
import {
  fetchApplicantProfile as apiFetchApplicantProfile,
  updateApplicantProfile as apiUpdateApplicantProfile,
  addWorkHistoryJob as apiAddWorkHistoryJob,
  editWorkHistoryJob as apiEditWorkHistoryJob,
  deleteWorkHistoryJob as apiDeleteWorkHistoryJob
} from 'api/applicantProfile';
import type { WorkHistoryJob } from 'api/applicantProfile';
import type {
  Dispatch as StaffDispatch,
  GetState as StaffGetState
} from 'redux/staff';
import { pushFlashMessage } from './flashMessages';
import type { Dispatch, GetState } from '..';

type FetchCurrentUserRequestAction = {
  type: 'CURRENT_USER_FETCH_REQUEST'
};
type FetchCurrentUserSuccessAction = {
  type: 'CURRENT_USER_FETCH_SUCCESS',
  currentUser: CurrentUser
};
type UpdateCurrentUserWith = {
  type: 'CURRENT_USER_UPDATE_PARTIAL',
  currentUserPartial: Object
};
type UpdateSearchLocationAction = {
  type: 'CURRENT_USER_UPDATE_SEARCH_LOCATION',
  location: SearchLocation
};
type ClearSearchLocationAction = {
  type: 'CURRENT_USER_CLEAR_SEARCH_LOCATION'
};
type SaveListingAction = {
  type: 'SAVE_LISTING',
  listingId: string,
  listingType: string
};
type UnsaveListingAction = { type: 'UNSAVE_LISTING', listingId: string };
type SaveSearchAction = {
  type: 'SAVE_SEARCH',
  savedSearch: SavedSearch
};
type UnsaveSearchAction = { type: 'UNSAVE_SEARCH', searchId: string };
type FetchApplicantRequestAction = {
  type: 'APPLICANT_PROFILE_FETCH_REQUEST'
};
type FetchApplicantSuccessAction = {
  type: 'APPLICANT_PROFILE_FETCH_SUCCESS',
  currentUserPartial: Object
};
type FetchApplicantFailureAction = {
  type: 'APPLICANT_PROFILE_FETCH_FAILURE'
};

export type Action =
  | FetchCurrentUserRequestAction
  | FetchCurrentUserSuccessAction
  | UpdateCurrentUserWith
  | UpdateSearchLocationAction
  | ClearSearchLocationAction
  | SaveListingAction
  | UnsaveListingAction
  | SaveSearchAction
  | UnsaveSearchAction
  | FetchApplicantRequestAction
  | FetchApplicantSuccessAction
  | FetchApplicantFailureAction;

const fetchCurrentUserRequest = (): FetchCurrentUserRequestAction => ({
  type: 'CURRENT_USER_FETCH_REQUEST'
});

const fetchCurrentUserSuccess = (
  currentUser: CurrentUser
): FetchCurrentUserSuccessAction => ({
  type: 'CURRENT_USER_FETCH_SUCCESS',
  currentUser
});

export const fetchCurrentUser = () => (
  dispatch: Dispatch,
  getState: GetState
) => {
  const currentUser = getState().currentUser;

  if (currentUser && currentUser.isFetching) {
    return (Promise.resolve(): Promise<void>);
  }

  dispatch(fetchCurrentUserRequest());
  return apiFetchCurrentUser().then(fetchedUser =>
    dispatch(fetchCurrentUserSuccess(fetchedUser))
  );
};

export const fetchCurrentUserForStaff = () => (
  dispatch: StaffDispatch,
  getState: StaffGetState
) => {
  const currentUser = getState().currentUser;

  if (currentUser && currentUser.isFetching) {
    return (Promise.resolve(): Promise<void>);
  }

  dispatch(fetchCurrentUserRequest());
  return apiFetchCurrentUser().then(fetchedUser =>
    dispatch(fetchCurrentUserSuccess(fetchedUser))
  );
};

export const updateCurrentUser = fetchCurrentUserSuccess;

export const updateCurrentUserWith = (
  currentUserPartial: Object
): UpdateCurrentUserWith => ({
  type: 'CURRENT_USER_UPDATE_PARTIAL',
  currentUserPartial
});

export const updateSearchLocation = (
  location: SearchLocation,
  didUserChange: boolean
) => (dispatch: Dispatch) => {
  if (didUserChange) {
    trackEvent('Changed Search Location', extractLocationProperties(location));
  }
  return apiUpdateSearchLocation(location).then(updatedLocation =>
    dispatch({
      type: 'CURRENT_USER_UPDATE_SEARCH_LOCATION',
      location: updatedLocation
    })
  );
};

export const clearSearchLocation = (didUserChange: boolean) => (
  dispatch: Dispatch
) => {
  if (didUserChange) {
    trackEvent('Cleared Search Location');
  }
  dispatch({ type: 'CURRENT_USER_CLEAR_SEARCH_LOCATION' });
  return apiClearSearchLocation();
};

export const saveListing = (
  listingId: string,
  listingType: string,
  listingName: string
) => (dispatch: Dispatch) => {
  dispatch({ type: 'SAVE_LISTING', listingId, listingType });
  if (listingType === 'ORG') {
    dispatch(
      pushFlashMessage({
        type: 'SAVE_ORG',
        data: { orgName: listingName }
      })
    );
  } else if (listingType === 'GROUP') {
    dispatch(
      pushFlashMessage({
        type: 'SAVE_GROUP',
        data: { groupName: listingName }
      })
    );
  }
  return apiSaveListing(listingId, listingType).catch(error => {
    dispatch({ type: 'UNSAVE_LISTING', listingId, listingType });
    throw error;
  });
};

export const unsaveListing = (
  listingId: string,
  listingType: string,
  listingName: string
) => (dispatch: Dispatch) => {
  dispatch({ type: 'UNSAVE_LISTING', listingId });
  if (listingType === 'ORG') {
    dispatch(
      pushFlashMessage({
        type: 'UNSAVE_ORG',
        data: { orgName: listingName }
      })
    );
  } else if (listingType === 'GROUP') {
    dispatch(
      pushFlashMessage({
        type: 'UNSAVE_GROUP',
        data: { groupName: listingName }
      })
    );
  }
  return apiUnsaveListing(listingId, listingType).catch(error => {
    // if listing was already removed, ignore error
    if (error.statusCode !== 404) {
      dispatch({ type: 'SAVE_LISTING', listingId, listingType });
      throw error;
    }
  });
};

export const saveSearch = (search: SavedSearchUnsaved) => (
  dispatch: Dispatch
) =>
  apiSaveSearch(search).then(savedSearch => {
    dispatch({ type: 'SAVE_SEARCH', savedSearch });
    dispatch(pushFlashMessage({ type: 'SAVED_SEARCH' }));
    trackEvent('Created Email Alert');
  });

export const unsaveSearch = (searchId: string) => (dispatch: Dispatch) =>
  apiUnsaveSearch(searchId).then(() => {
    dispatch({ type: 'UNSAVE_SEARCH', searchId });
    dispatch(pushFlashMessage({ type: 'UNSAVED_SEARCH' }));
  });

export const setABExperimentBucket = (experimentId: string, bucket: string) => (
  dispatch: Dispatch,
  getState: GetState
) => {
  const currentUser = cloneDeep(getState().currentUser);
  return apiSetABExperimentBucket(experimentId, bucket).then(abExperiments => {
    currentUser.abExperiments = abExperiments;
    dispatch(updateCurrentUser(currentUser));
    return abExperiments;
  });
};

const dispatchProfileUpdate = (request: Function, dispatch: Dispatch) => {
  dispatch({ type: 'APPLICANT_PROFILE_FETCH_REQUEST' });
  return request()
    .then(currentUserPartial => {
      dispatch({
        type: 'APPLICANT_PROFILE_FETCH_SUCCESS',
        currentUserPartial
      });
    })
    .catch(error => {
      dispatch({ type: 'APPLICANT_PROFILE_FETCH_FAILURE' });
      throw error;
    });
};

export const fetchApplicantProfile = () => (dispatch: Dispatch) =>
  dispatchProfileUpdate(() => apiFetchApplicantProfile(), dispatch);

export const updateApplicantProfile = (values: Object) => (
  dispatch: Dispatch
) => dispatchProfileUpdate(() => apiUpdateApplicantProfile(values), dispatch);

export const updateWorkHistoryJob = (job: WorkHistoryJob, id: ?string) => (
  dispatch: Dispatch
) => {
  const submit = () =>
    id ? apiEditWorkHistoryJob(id, job) : apiAddWorkHistoryJob(job);

  return dispatchProfileUpdate(() => submit(), dispatch);
};

export const deleteWorkHistoryJob = (id: string) => (dispatch: Dispatch) =>
  dispatchProfileUpdate(() => apiDeleteWorkHistoryJob(id), dispatch);
